import React, { createContext, ReactElement, useEffect, useState } from 'react';

import { PublicCopilotLayout } from '@/components/PublicCopilotLayout';
import { AsaphButton } from '@/components/shared/Button';
import { SentryDeploymentContext, SentryTag } from '@/config/constants';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import * as Sentry from '@sentry/react';
import {
	OptionsObject,
	SnackbarKey,
	SnackbarMessage,
	useSnackbar,
} from 'notistack';

import CopilotAuthenticateUser from './components/CopilotAuthenticateUser';
import CopilotCreateUser from './components/CopilotCreateUser';
import CopilotError from './components/CopilotError';
import CopilotFetchSongs from './components/CopilotFetchSongs';
import CopilotLinkUser from './components/CopilotLinkUser/CopilotLinkUser';
import CopilotLoggingIn from './components/CopilotLoggingIn';
import CopilotPrepareSongs from './components/CopilotPrepareSongs';
import CopilotUserNotFound from './components/CopilotUserNotFound';
import RenderCopilotIntro from './components/RenderCopilotIntro';
import RenderCopilotSetup from './components/RenderCopilotSetup';
import styles from './CopilotPage.module.scss';
import { PcoSongsMessage } from './types';

const PCO_KEY_PREFIX = 'PCO';
const COPILOT_KEY_PREFIX = 'CPLT';

type PcoEvent = MessageEvent<{ key: string; data: unknown }>;

type SongInput = {
	name?: string;
	artist?: string;
	ccli?: number;
	recordedKey?: string | undefined;
	lyrics?: string;
	author?: string;
	themeTags: string[];
};

type PcoAuthData = {
	email?: string;
	id: number;
	organisationId: number;
	teamName: string;
	displayName: string;
};

export const sendMessageToPco = ({
	key,
	data,
}: {
	key: string;
	data?: unknown;
}) => {
	window.parent.postMessage(
		{ key: `${COPILOT_KEY_PREFIX}_${key}`, data },
		'https://services.planningcenteronline.com',
	);
};

export const checkPcoEvent = (event, key) => {
	if (event.data && event.data.key === `${PCO_KEY_PREFIX}_${key}`) {
		console.log('RECEIVED EVENT FROM PCO', `${PCO_KEY_PREFIX}_${key}`);
		return true;
	}
	return false;
};

export enum CopilotState {
	AuthUser,
	UserNotFound,
	CreateUser,
	LinkUser,
	PreparingSongs,
	ImportingSongs,
	LoggingIn,
	Error,
}

type CopilotContext = {
	state: CopilotState;
	pcoSongs: SongInput[];
	token: string | null;
	authData: PcoAuthData | null;
	enqueueSnackbar: (
		message: SnackbarMessage,
		options?: OptionsObject | undefined,
	) => SnackbarKey;
	setState: React.Dispatch<React.SetStateAction<CopilotState>>;
	setToken: React.Dispatch<React.SetStateAction<string | null>>;
};

const defaultValue: CopilotContext = {
	state: CopilotState.AuthUser,
	pcoSongs: [],
	token: null,
	authData: null,
	enqueueSnackbar: () => '',
	setState: () => {},
	setToken: () => {},
};

export const CopilotContext = createContext<CopilotContext>(defaultValue);

const CopilotPage = () => {
	const { enqueueSnackbar } = useSnackbar();
	const {
		api: { url },
	} = useEnvironment();
	const { handleError } = useErrorHandler();
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const [completedActions, setCompletedActions] = useState<string[]>([]);
	const [token, setToken] = useState<string | null>(null);
	const [state, setState] = useState<CopilotState>(CopilotState.AuthUser);
	const [pcoSongs, setPcoSongs] = useState<SongInput[]>([]);
	const [authData, setAuthData] = useState(defaultValue.authData);

	useEffect(() => {
		Sentry.setTag(SentryTag.DEPLOYMENT, SentryDeploymentContext.EXTENSION);
	});

	// When the component and the relevant dependencies are ready, bind
	// an event listener to receive and handle messages from the parent window (PCO)
	useEffect(() => {
		const handleMessage = async (event: PcoEvent) => {
			// Do we trust the sender of this message?
			if (event.origin !== 'https://services.planningcenteronline.com')
				return;

			// enqueueSnackbar(`Message received: ${event.data.key}`);

			// if (checkPcoEvent(event, 'CHECK_ACCEPTED_TERMS')) {
			// 	if (event.data.data) {
			// 		setAcceptedTerms(true);
			// 		sendMessageToPco({ key: 'USER_ACCEPTED_TERMS' });
			// 	}
			// }

			if (checkPcoEvent(event, 'AUTHENTICATE')) {
				const authData = event.data.data as PcoAuthData;
				setAuthData(authData);
				setState(CopilotState.AuthUser);
			}

			if (checkPcoEvent(event, 'ADD_SONGS')) {
				// Change the state to PreparingSongs
				setState((state) => {
					if (state !== CopilotState.PreparingSongs) {
						return CopilotState.PreparingSongs;
					}
					return state;
				});

				const addSongsMessage = event.data.data as PcoSongsMessage;
				const songs = addSongsMessage.songs.map<SongInput>((song) => ({
					lyrics: song.lyrics ?? undefined,
					name: song.name ?? undefined,
					author: song.author ?? undefined,
					artist: song.artist ?? undefined,
					ccli: song.ccli ?? undefined,
					recordedKey: song.recordedKey ?? undefined,
					themeTags: song.themeTags,
				}));
				setPcoSongs((currentSongs) => [...currentSongs, ...songs]);
				// Once all songs have finished fetching, change to
				// importing songs or authenticating
				if (addSongsMessage.isFinalChunk) {
					setState(
						songs.length
							? CopilotState.ImportingSongs
							: CopilotState.LoggingIn,
					);
				}
			}
		};

		console.log('Binding event listener');
		window.addEventListener('message', handleMessage);
		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [enqueueSnackbar, handleError, token, url]);

	const acceptTerms = () => {
		setAcceptedTerms(true);
		sendMessageToPco({ key: 'USER_ACCEPTED_TERMS' });
	};

	return (
		<PublicCopilotLayout
			footer={
				!acceptedTerms && (
					<div className={styles['accept-row']}>
						{/* <AsaphButton label="How it works?" color="grey" /> */}
						<AsaphButton
							onClick={acceptTerms}
							label="Allow access"
							color="blue"
							fullWidth
						/>
					</div>
				)
			}
		>
			<CopilotContext.Provider
				value={{
					state,
					pcoSongs,
					token,
					authData,
					enqueueSnackbar,
					setToken,
					setState,
				}}
			>
				{!acceptedTerms ? (
					<RenderCopilotIntro />
				) : (
					<RenderCopilotSetup>
						<RenderCopilotState state={state} />
					</RenderCopilotSetup>
				)}
			</CopilotContext.Provider>
		</PublicCopilotLayout>
	);
};

export default CopilotPage;

function RenderCopilotState({ state }: { state: CopilotState }): ReactElement {
	switch (state) {
		case CopilotState.AuthUser:
			return <CopilotAuthenticateUser />;
		case CopilotState.UserNotFound:
			return <CopilotUserNotFound />;
		case CopilotState.CreateUser:
			return <CopilotCreateUser />;
		case CopilotState.LinkUser:
			return <CopilotLinkUser />;
		case CopilotState.PreparingSongs:
			return <CopilotPrepareSongs />;
		case CopilotState.ImportingSongs:
			return <CopilotFetchSongs />;
		case CopilotState.LoggingIn:
			return <CopilotLoggingIn />;
		case CopilotState.Error:
		default:
			return <CopilotError />;
	}
}
